import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import FormCheckbox from './FormCheckbox.jsx';
import FormToggle from './FormToggle.jsx';

function AdvancedFilters({
  meta: { categories, accessibilityAccess, priceRange },
  maxPrice, minPrice, setMaxPrice, setMinPrice,
  requiredAccessibilityAccess, setRequiredAccessibilityAccess,
  selectedCategories, setSelectedCategories,
}) {
  const nameLeft = 'min-price';
  const nameRight = 'max-price';
  const step = 50;
  const ratio = 100 / ((priceRange.max + 10) - priceRange.min);
  const posLeft = (ratio * minPrice) - (ratio * priceRange.min);
  const posRight = (ratio * maxPrice) - (ratio * priceRange.min);

  const rangeA = useRef(null);
  const rangeB = useRef(null);
  const inverseLeft = useRef(null);
  const inverseRight = useRef(null);
  const range = useRef(null);
  const thumbLeft = useRef(null);
  const thumbRight = useRef(null);
  const valueLeft = useRef(null);
  const valueRight = useRef(null);

  const updateMinPrice = (e) => setMinPrice(parseInt(e.target.value, 10));
  const updateMaxPrice = (e) => setMaxPrice(parseInt(e.target.value, 10));

  const setLeftValue = () => {
    const newValue = Math.min(rangeA.current.value, rangeB.current.value - step);
    setMinPrice(parseInt(newValue, 10));
    rangeA.current.value = newValue;

    const ratioLeft = 100 / (rangeA.current.max - rangeA.current.min);
    const value = Number((ratioLeft * rangeA.current.value) - (ratioLeft * rangeA.current.min)).toFixed(1);

    range.current.style.left = `${value}%`;
    thumbLeft.current.style.left = `${value}%`;
  };

  const setRightValue = () => {
    const newValue = Math.max(rangeB.current.value, rangeA.current.value - -step);
    setMaxPrice(parseInt(newValue, 10));
    rangeB.current.value = newValue;

    const ratioRight = 100 / (rangeB.current.max - rangeB.current.min);
    const value = (ratioRight * rangeB.current.value) - (ratioRight * rangeB.current.min);

    range.current.style.right = `${100 - value}%`;
    thumbRight.current.style.left = `${value}%`;
  };

  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const toggleSelectedCategory = (category) => {
    const index = selectedCategories.indexOf(category);
    const selectedCategoriesCopy = [...selectedCategories];

    if (index >= 0) {
      selectedCategoriesCopy.splice(index, 1);
    } else {
      selectedCategoriesCopy.push(category);
    }

    setSelectedCategories(selectedCategoriesCopy);
  };

  const resetFilters = () => {
    setMaxPrice(priceRange.max);
    setMinPrice(priceRange.min);
    setRequiredAccessibilityAccess(false);
    setSelectedCategories([]);
  };

  const generateCheckboxes = () => {
    const allCategories = [...categories];
    const elements = [];

    for (let index = 0; index < allCategories.length; index = +index) {
      if (index % 2 === 0) {
        elements.push( // eslint-disable-line function-paren-newline
          <div className="c-ticket-selector__filter-segment" key={`${allCategories[index].title}-${index}`}>
            {allCategories.splice(index, 2).map((category) => {
              const checked = selectedCategories.includes(category.value);

              return (
                <div className="c-ticket-selector__filter-field" key={category.title}>
                  <FormCheckbox
                    checked={checked}
                    dark
                    id={`${category.value}-filter`}
                    label={category.title}
                    name={category.value}
                    onChange={toggleSelectedCategory}
                    value="1"
                  />
                </div>
              );
            })}
          </div>,
        ); // eslint-disable-line function-paren-newline
      }
    }

    return elements;
  };

  return (
    <section className="o-section">
      <div className="o-container">
        <div className="o-grid">
          <div className="o-col">
            <div className="c-ticket-selector__show-filters">
              <button
                className="c-button"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                type="button"
              >
                {filtersExpanded ? 'Close filters' : 'View all filters'}
              </button>
            </div>

            <div className={`c-ticket-selector__filter ${!filtersExpanded ? 'u-flex-at-medium' : ''}`}>
              <p className="c-ticket-selector__filter-title">All Filters</p>

              <div className="c-ticket-selector__filter-options-mobile">
                <p className="c-ticket-selector__subtitle">Categories</p>

                <div className="c-ticket-selector__filter-field">
                  <FormToggle
                    id={`${accessibilityAccess.value}-mobile`}
                    name={accessibilityAccess.value}
                    label={accessibilityAccess.title}
                    onChange={setRequiredAccessibilityAccess}
                    value={requiredAccessibilityAccess}
                  />
                </div>

                {categories.map((category) => {
                  const checked = selectedCategories.includes(category.value);

                  return (
                    <div className="c-ticket-selector__filter-field" key={category.title}>
                      <FormCheckbox
                        checked={checked}
                        dark
                        id={`${category.value}-filter-mobile`}
                        label={category.title}
                        name={category.value}
                        onChange={toggleSelectedCategory}
                        value="1"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="c-ticket-selector__filter-options">
                <div className="c-ticket-selector__filter-segment">
                  <p className="c-ticket-selector__subtitle">Categories</p>
                  <div className="c-ticket-selector__filter-field">
                    <FormToggle
                      id={accessibilityAccess.value}
                      name={accessibilityAccess.value}
                      label={accessibilityAccess.title}
                      onChange={setRequiredAccessibilityAccess}
                      value={requiredAccessibilityAccess}
                    />
                  </div>
                </div>

                {generateCheckboxes()}
              </div>

              <div className="c-ticket-selector__price-range-wrapper">
                <p className="c-ticket-selector__subtitle">Price range</p>

                <div className="c-ticket-selector__price-range">
                  <div className="c-multi-range-slider" id="slider-distance">
                    <div className="c-multi-range-slider__wrapper">
                      <div
                        className="c-multi-range-slider__inverse-left"
                        ref={inverseLeft}
                        style={{
                          width: '100%',
                        }}
                      />
                      <div
                        className="c-multi-range-slider__inverse-right"
                        ref={inverseRight}
                        style={{
                          width: '100%',
                        }}
                      />
                      <div
                        className="c-multi-range-slider__range"
                        ref={range}
                        style={{
                          left: posLeft.toString().concat('%'),
                          right: (100 - posRight).toString().concat('%'),
                        }}
                      />
                      <span
                        className="c-multi-range-slider__thumb"
                        ref={thumbLeft}
                        style={{
                          left: posLeft.toString().concat('%'),
                        }}
                      />
                      <span
                        className="c-multi-range-slider__thumb"
                        ref={thumbRight}
                        style={{
                          left: posRight.toString().concat('%'),
                        }}
                      />
                      <div className="c-multi-range-slider__label-wrapper">
                        <span ref={valueLeft}>
                          $
                          {minPrice}
                        </span>
                        <span ref={valueRight}>
                          $
                          {maxPrice}
                        </span>
                      </div>
                    </div>

                    <input
                      max={priceRange.max + 10}
                      min={priceRange.min}
                      name={nameLeft}
                      onInput={setLeftValue}
                      onMouseUp={updateMinPrice}
                      onTouchEndCapture={updateMinPrice}
                      step={step}
                      ref={rangeA}
                      tabIndex={0}
                      type="range"
                      value={minPrice}
                    />

                    <input
                      max={priceRange.max + 10}
                      min={priceRange.min}
                      name={nameRight}
                      onInput={setRightValue}
                      onMouseUp={updateMaxPrice}
                      onTouchEndCapture={updateMaxPrice}
                      step={step}
                      ref={rangeB}
                      tabIndex={0}
                      type="range"
                      value={maxPrice}
                    />
                  </div>
                </div>
              </div>

              <div className="c-ticket-selector__buttons">
                <button
                  className="c-button u-hide-at-medium"
                  onClick={() => setFiltersExpanded(false)}
                  type="button"
                >
                  Close
                </button>

                <button
                  className="c-text-link c-text-link--dark u-spacing-left-right-xsmall"
                  onClick={() => resetFilters()}
                  onKeyDown={() => resetFilters()}
                  tabIndex={0}
                  type="button"
                >
                  Reset filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

AdvancedFilters.propTypes = {
  meta: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    accessibilityAccess: PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    priceRange: PropTypes.shape({
      max: PropTypes.number.isRequired,
      min: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  maxPrice: PropTypes.number.isRequired,
  minPrice: PropTypes.number.isRequired,
  requiredAccessibilityAccess: PropTypes.bool.isRequired,
  selectedCategories: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
  setMaxPrice: PropTypes.func.isRequired,
  setMinPrice: PropTypes.func.isRequired,
  setRequiredAccessibilityAccess: PropTypes.func.isRequired,
  setSelectedCategories: PropTypes.func.isRequired,
};

export default AdvancedFilters;
